import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, Copy, Check, FileText, Download, Search, Users, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { clearAuth, authFetch } from "@/lib/auth";
import { PERSONA_META, useLang, LangToggle } from "@/components/landing/i18n";

const UI = {
  en: {
    portal: "Institution Portal",
    signOut: "Sign out",
    joinCode: "Student join code",
    joinCodeSub: "Students enter this code while signing up, or later from their dashboard, to link their reports to you.",
    copy: "Copy",
    copied: "Copied",
    statStudents: "Students linked",
    statReports: "Reports completed",
    searchPlaceholder: "Search by name or email",
    exportCsv: "Export CSV",
    downloadZip: (n) => `Download ZIP (${n})`,
    thStudent: "Student",
    thGrade: "Grade",
    thClassic: "Classic report",
    thDiscovery: "Discovery report",
    thJoined: "Joined",
    empty: "No students linked yet. Share your join code to get started.",
    noMatch: "No students match your search.",
    pdfFail: "PDF download failed",
    zipFail: "ZIP download failed",
    csvOk: "Student CSV downloaded",
    apt: "aptitude",
    none: "—",
  },
  hi: {
    portal: "संस्थान पोर्टल",
    signOut: "साइन आउट",
    joinCode: "स्टूडेंट जॉइन कोड",
    joinCodeSub: "विद्यार्थी साइन अप करते समय या बाद में अपने डैशबोर्ड से यह कोड डालकर अपनी रिपोर्ट आपसे जोड़ते हैं।",
    copy: "कॉपी",
    copied: "कॉपी हो गया",
    statStudents: "जुड़े विद्यार्थी",
    statReports: "पूरी हुई रिपोर्ट",
    searchPlaceholder: "नाम या ईमेल से खोजें",
    exportCsv: "CSV निर्यात करें",
    downloadZip: (n) => `ZIP डाउनलोड (${n})`,
    thStudent: "विद्यार्थी",
    thGrade: "कक्षा",
    thClassic: "क्लासिक रिपोर्ट",
    thDiscovery: "डिस्कवरी रिपोर्ट",
    thJoined: "जुड़े",
    empty: "अभी कोई विद्यार्थी जुड़ा नहीं है। शुरुआत के लिए अपना जॉइन कोड साझा करें।",
    noMatch: "आपकी खोज से कोई विद्यार्थी नहीं मिला।",
    pdfFail: "PDF डाउनलोड विफल",
    zipFail: "ZIP डाउनलोड विफल",
    csvOk: "स्टूडेंट CSV डाउनलोड हुई",
    apt: "योग्यता",
    none: "—",
  },
};

const fmtDate = (iso, loc = "en-GB") => (iso ? new Date(iso).toLocaleDateString(loc, { day: "2-digit", month: "short", year: "numeric" }) : "—");

async function saveBlob(res, fallbackName) {
  if (!res.ok) throw new Error("download failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  a.download = m ? m[1] : fallbackName;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function InstitutionDashboard({ onLogout }) {
  const { lang } = useLang();
  const ui = UI[lang] || UI.en;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState({});
  const [copied, setCopied] = useState(false);
  const [busyZip, setBusyZip] = useState(false);

  useEffect(() => {
    authFetch("/api/institution/students").then(async (r) => {
      if (r.status === 401 || r.status === 403) {
        clearAuth();
        setErr(true);
        onLogout?.();
        return;
      }
      setData(await r.json());
    }).catch(() => setErr(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const students = useMemo(() => {
    const list = data?.students || [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) => s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q));
  }, [data, query]);

  const selIds = Object.keys(selected).filter((k) => selected[k]);
  const allChecked = students.length > 0 && students.every((s) => selected[s.id]);
  const reportCount = (data?.students || []).reduce((n, s) => n + s.classicReports.length + s.discoveryReports.length, 0);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.institution.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error(data.institution.code); }
  };

  const downloadPdf = async (kind, rid) => {
    try {
      await saveBlob(await authFetch(`/api/institution/reports/${kind}/${rid}/pdf?lang=${lang}`), `brightiq-${kind}-report.pdf`);
    } catch { toast.error(ui.pdfFail); }
  };

  const downloadZip = async () => {
    const items = [];
    for (const s of data.students) {
      if (!selected[s.id]) continue;
      if (s.classicReports[0]) items.push({ kind: "classic", id: s.classicReports[0].id });
      if (s.discoveryReports[0]) items.push({ kind: "discovery", id: s.discoveryReports[0].id });
    }
    if (!items.length) return;
    setBusyZip(true);
    try {
      await saveBlob(await authFetch("/api/institution/reports/download", { method: "POST", body: JSON.stringify({ items }) }), "institution-reports.zip");
    } catch { toast.error(ui.zipFail); }
    setBusyZip(false);
  };

  const exportCsv = async () => {
    try {
      await saveBlob(await authFetch("/api/institution/students.csv"), "students.csv");
      toast.success(ui.csvOk);
    } catch { toast.error(ui.zipFail); }
  };

  if (err && !data) return null;
  if (!data) return <div className="min-h-screen bg-paper flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-iqblue" data-testid="inst-dash-loading" /></div>;

  const inst = data.institution;

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="institution-dashboard">
      <header className="max-w-6xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="inst-dash-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span> <span className="text-ink/35 font-body text-xs font-semibold tracking-[0.2em] uppercase ml-1">{ui.portal}</span></span>
        </Link>
        <div className="flex items-center gap-4">
          <LangToggle />
          <button onClick={() => { clearAuth(); try { localStorage.removeItem("biq_institution"); } catch {} onLogout?.(); }} data-testid="inst-logout-button" className="flex items-center gap-1.5 text-xs font-semibold text-ink/50 hover:text-iqpink transition-colors">
            <LogOut className="h-3.5 w-3.5" /> {ui.signOut}
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight" data-testid="inst-dash-name">{inst.name}</h1>
          <p className="mt-2 text-sm text-ink/55">{[inst.curriculum, inst.designation, inst.officialName].filter(Boolean).join(" · ")}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08, duration: 0.5 }} className="mt-8 rounded-3xl bg-ink text-paper p-7 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="inst-code-banner">
          <div className="flex-1">
            <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-iqcyan">{ui.joinCode}</div>
            <div className="font-display font-bold text-3xl tracking-[0.25em] mt-2" data-testid="inst-join-code">{inst.code}</div>
            <p className="mt-2 text-sm text-paper/60 max-w-md">{ui.joinCodeSub}</p>
          </div>
          <button onClick={copyCode} data-testid="inst-copy-code-button" className="shrink-0 flex items-center gap-2 bg-paper text-ink font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-iqcyan transition-colors duration-300">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? ui.copied : ui.copy}
          </button>
        </motion.div>

        <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
          <div className="rounded-3xl bg-cream border border-ink/10 p-5" data-testid="inst-stat-students">
            <div className="flex items-center gap-2 font-display font-bold text-2xl"><Users className="h-5 w-5 text-iqblue" /> {data.students.length}</div>
            <div className="text-xs text-ink/50 mt-1">{ui.statStudents}</div>
          </div>
          <div className="rounded-3xl bg-cream border border-ink/10 p-5" data-testid="inst-stat-reports">
            <div className="flex items-center gap-2 font-display font-bold text-2xl"><GraduationCap className="h-5 w-5 text-iqamber" /> {reportCount}</div>
            <div className="text-xs text-ink/50 mt-1">{ui.statReports}</div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/35" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={ui.searchPlaceholder} data-testid="inst-search-input"
              className="w-full rounded-full border border-ink/15 bg-white pl-11 pr-4 py-3 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35" />
          </div>
          <div className="flex gap-2.5">
            <button onClick={exportCsv} data-testid="inst-export-csv" className="flex items-center gap-2 border border-ink/15 font-semibold text-xs px-5 py-3 rounded-full hover:border-iqblue hover:text-iqblue transition-colors duration-300">
              <Download className="h-3.5 w-3.5" /> {ui.exportCsv}
            </button>
            <button onClick={downloadZip} disabled={!selIds.length || busyZip} data-testid="inst-download-zip"
              className="flex items-center gap-2 bg-ink text-paper font-semibold text-xs px-5 py-3 rounded-full hover:bg-iqblue disabled:opacity-40 transition-colors duration-300">
              {busyZip ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} {ui.downloadZip(selIds.length)}
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-3xl bg-cream border border-ink/10 overflow-hidden" data-testid="inst-students-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-ink/45 uppercase tracking-wider">
                  <th className="pl-6 pr-2 py-3.5 w-8">
                    <input type="checkbox" checked={allChecked} onChange={() => setSelected(allChecked ? {} : Object.fromEntries(students.map((s) => [s.id, true])))} data-testid="inst-select-all" className="h-3.5 w-3.5 accent-[#1F5BFF]" />
                  </th>
                  <th className="px-3 py-3.5 font-semibold">{ui.thStudent}</th>
                  <th className="px-3 py-3.5 font-semibold">{ui.thGrade}</th>
                  <th className="px-3 py-3.5 font-semibold">{ui.thClassic}</th>
                  <th className="px-3 py-3.5 font-semibold">{ui.thDiscovery}</th>
                  <th className="px-3 py-3.5 font-semibold">{ui.thJoined}</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const c = s.classicReports[0];
                  const d = s.discoveryReports[0];
                  return (
                    <tr key={s.id} className="border-t border-ink/5 hover:bg-white/60 transition-colors" data-testid={`inst-student-row-${s.id}`}>
                      <td className="pl-6 pr-2 py-3">
                        <input type="checkbox" checked={!!selected[s.id]} onChange={() => setSelected({ ...selected, [s.id]: !selected[s.id] })} data-testid={`inst-select-${s.id}`} className="h-3.5 w-3.5 accent-[#1F5BFF]" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-ink/85 text-sm">{s.name}</div>
                        <div className="text-ink/45">{s.email}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-ink/70">{s.grade || ui.none}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {c ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="font-semibold" style={{ color: PERSONA_META[c.persona]?.color || "#1F5BFF" }}>{PERSONA_META[c.persona]?.[lang]?.name || PERSONA_META[c.persona]?.en.name || c.persona}</span>
                            <span className="text-ink/45">{c.aptPct}%</span>
                            <button onClick={() => downloadPdf("classic", c.id)} data-testid={`inst-pdf-classic-${s.id}`} className="h-7 w-7 rounded-full border border-ink/15 inline-flex items-center justify-center text-ink/55 hover:border-iqblue hover:text-iqblue transition-colors" aria-label="Download classic PDF">
                              <FileText className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ) : <span className="text-ink/35">{ui.none}</span>}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {d ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="font-semibold text-ink/80">{d.archetype}</span>
                            <span className="text-ink/45">{d.aptitude}% {ui.apt}</span>
                            <button onClick={() => downloadPdf("discovery", d.id)} data-testid={`inst-pdf-discovery-${s.id}`} className="h-7 w-7 rounded-full border border-ink/15 inline-flex items-center justify-center text-ink/55 hover:border-iqamber hover:text-iqamber transition-colors" aria-label="Download discovery PDF">
                              <FileText className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ) : <span className="text-ink/35">{ui.none}</span>}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-ink/55">{fmtDate(s.joinedAt, lang === "hi" ? "hi-IN" : "en-GB")}</td>
                    </tr>
                  );
                })}
                {students.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-10 text-center text-ink/40" data-testid="inst-students-empty">{query ? ui.noMatch : ui.empty}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
